// components/chat-surface/chat-input-engine.js
// ═══════════════════════════════════════════════════════════
// Complete input engine: wraps ChatInputBar and adds:
//   - Slash command detection + picker
//   - All input state management
//   - File handling delegation
//   - Paste stripping (built into ChatInputBar)
//
// Usage: ChatSurface creates one of these. Features never touch it.
// ═══════════════════════════════════════════════════════════

import { useState, useRef } from 'react';
import ChatInputBar from '../chat-input-bar';
import ChatSlashPicker, { DEFAULT_COMMANDS } from './chat-slash-picker';

export default function ChatInputEngine({
  inputRef,
  value,
  setValue,
  onSend,
  loading,
  onAbort,

  // File
  pendingFile,
  setPendingFile,
  onFileSelect,
  filesAtLimit,

  // Deep modes
  deepSearchActive,
  setDeepSearchActive,
  deepModeArmed,
  setDeepModeArmed,

  // Voice
  voiceListening,
  voiceEnabled,
  onVoiceToggle,

  // Slash commands
  enableSlashCommands = true,
  slashCommands,
  onSlashCommand,

  // Customization
  placeholder = 'Message…',
}) {
  const [showSlashPicker, setShowSlashPicker] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');

  function handleInputChange(val) {
    if (!enableSlashCommands) return;
    const trimmed = (val || '').trim();
    if (trimmed.startsWith('/') && trimmed.length > 1) {
      setSlashQuery(trimmed.slice(1).toLowerCase());
      setShowSlashPicker(true);
    } else {
      setShowSlashPicker(false);
      setSlashQuery('');
    }
  }

  function handleSlashSelect(cmd) {
    setShowSlashPicker(false);
    setSlashQuery('');

    // Let the parent handle custom command behavior if provided
    if (onSlashCommand) {
      onSlashCommand(cmd, { setValue, setDeepModeArmed, inputRef });
      return;
    }

    // Default command behaviors
    const prompts = {
      canvas: 'Build on the canvas: ',
      compare: 'Compare these two options side by side: ',
      table: 'Organize this into a table on the canvas: ',
    };
    const autoSend = {
      summarize: 'Summarize our conversation so far — key points, decisions made, and open questions.',
      plan: 'Create an action plan from what we discussed. Include clear next steps, owners, and deadlines.',
      export: 'Export our conversation as a clean document I can share.',
    };

    if (cmd.id === 'reset') {
      setValue('');
      if (inputRef?.current) inputRef.current.innerText = '';
      return;
    }
    if (cmd.id === 'deep') {
      if (setDeepModeArmed) setDeepModeArmed(true);
      setValue('');
      if (inputRef?.current) inputRef.current.innerText = '';
      return;
    }
    if (autoSend[cmd.id]) {
      setValue('');
      if (inputRef?.current) inputRef.current.innerText = '';
      onSend(autoSend[cmd.id]);
      return;
    }
    if (prompts[cmd.id]) {
      setValue(prompts[cmd.id]);
      if (inputRef?.current) {
        inputRef.current.innerText = prompts[cmd.id];
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(inputRef.current);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      inputRef?.current?.focus();
    }
  }

  return (
    <>
      {/* Slash picker */}
      {enableSlashCommands && (
        <div style={{ position: 'relative' }}>
          <ChatSlashPicker
            query={slashQuery}
            commands={slashCommands}
            onSelect={handleSlashSelect}
            visible={showSlashPicker}
          />
        </div>
      )}

      {/* Input bar */}
      <ChatInputBar
        inputRef={inputRef}
        value={value}
        setValue={setValue}
        onSend={() => onSend()}
        loading={loading}
        onAbort={onAbort}
        pendingFile={pendingFile}
        setPendingFile={setPendingFile}
        onFileSelect={onFileSelect}
        filesAtLimit={filesAtLimit}
        deepSearchActive={deepSearchActive}
        setDeepSearchActive={setDeepSearchActive}
        deepModeArmed={deepModeArmed}
        setDeepModeArmed={setDeepModeArmed}
        voiceListening={voiceListening}
        voiceEnabled={voiceEnabled}
        onVoiceToggle={onVoiceToggle}
        onInputChange={handleInputChange}
        placeholder={placeholder}
      />
    </>
  );
}
